import { InteractionEvents, KeyEventNames, InteractionHandler } from './InteractionHandler';
import { BasePlayer } from '../player/BasePlayer';
import { ControlMessage } from '../controlMessage/ControlMessage';
import { TouchControlMessage } from '../controlMessage/TouchControlMessage';
import MotionEvent from '../MotionEvent';
import ScreenInfo from '../ScreenInfo';
import { ScrollControlMessage } from '../controlMessage/ScrollControlMessage';
import { CommandControlMessage } from '../controlMessage/CommandControlMessage';
import { KeyCodeControlMessage } from '../controlMessage/KeyCodeControlMessage';
import DeviceMessage from '../googDevice/DeviceMessage';
import Point from '../Point';
import Position from '../Position';
import Size from '../Size';
import store from '../../store';
import { StreamClientLike } from '../streaming/StreamClientLike';
import { BroadcastBus } from './BroadcastBus';
import request from '@/utils';

const TAG = '[FeaturedTouchHandler]';

export interface InteractionHandlerListener {
	sendMessage : (message : ControlMessage) => void;
	sendKeyEvent ?: (code : string, action : 'down' | 'up', metaState : number) => void;
	sendBackspaceKey ?: () => void;
	sendCharacter ?: (char : string) => void;
}

interface AdaptedControlMessage {
	action : number;
	position ?: Position;
	pressure ?: number;
	buttons ?: number;
	pointerId ?: number;
}

interface BroadcastMessage {
	type : 'touch' | 'key' | 'scroll' | 'button_key_event' | 'ime_text';
	sourcePhoneId : string;
	timestamp : number;
	data : any;
}

export class FeaturedInteractionHandler extends InteractionHandler {
	public readonly listener : StreamClientLike & InteractionHandlerListener;
	public readonly player : BasePlayer;
	private readonly phoneId : string;
	private readonly groupId : string;
	private isMasterDevice : boolean;
	private broadcastBusOff ?: () => void;

	private static readonly touchEventsNames : InteractionEvents[] = [
		'touchstart', 'touchend', 'touchmove', 'touchcancel',
		'mousedown', 'mouseup', 'mousemove', 'wheel'
	];
	private static readonly keyEventsNames : KeyEventNames[] = ['keydown', 'keyup'];
	public static SCROLL_EVENT_THROTTLING_TIME = 30;
	private readonly storedFromMouseEvent = new Map<number, TouchControlMessage>();
	private readonly storedFromTouchEvent = new Map<number, TouchControlMessage>();
	private lastScrollEvent ?: { time : number; hScroll : number; vScroll : number };
	private ALLOWED_SCROLL_SELECTORS = ['.video', '#video-container'];

	constructor(player : BasePlayer, listener : StreamClientLike, phoneId : string, groupId : string = 'default',sourceType: 'single' | 'group_control' = 'single',isMasterDevice : boolean = false) {
		super(player, FeaturedInteractionHandler.touchEventsNames, FeaturedInteractionHandler.keyEventsNames);
		this.player = player;
		this.listener = listener as StreamClientLike & InteractionHandlerListener;
		this.phoneId = phoneId;
		this.groupId = groupId;
		this.isMasterDevice = isMasterDevice;
		console.info(`[FeaturedInteractionHandler] init phoneId=${phoneId} isMaster=${isMasterDevice} sourceType=${sourceType} mode=${(listener as any)?.mode}`);
		if(sourceType=='group_control'){
			// 注册到广播系统
			this.registerToBroadcast();
			// 监听广播消息
			this.setupBroadcastListener();
		}
		this.tag.addEventListener('mouseleave', this.onMouseLeave);
		this.tag.addEventListener('mouseenter', this.onMouseEnter);
	}

	// 注册到广播系统
	private registerToBroadcast() {
		if ((window as any).electronAPI) {
			(window as any).electronAPI.registerBroadcast({
				phoneId: this.phoneId,
				groupId: this.groupId,
				isMasterDevice:this.isMasterDevice
			});
		}
	}

	// 设置广播监听
	private setupBroadcastListener() {
		if ((window as any).electronAPI && typeof (window as any).electronAPI.onBroadcastControlMessage === 'function') {
			(window as any).electronAPI.onBroadcastControlMessage(this.handleBroadcastMessage.bind(this));
			return;
		}
		// 网页端：订阅进程内 EventBus
		const off = BroadcastBus.getInstance().on(this.handleBroadcastMessage.bind(this));
		this.broadcastBusOff = off;
	}

	// 处理收到的广播消息
	private handleBroadcastMessage(data : any) {
		const { sourcePhoneId, message, timestamp } = data;
		// 避免处理自己发送的消息
		if (sourcePhoneId === this.phoneId) return;
		console.info(`[FeaturedInteractionHandler][web-recv] slave=${this.phoneId} from=${sourcePhoneId} type=${message?.type} mode=${(this.listener as any)?.mode}`);
		// 根据消息类型执行相应的操作
		switch (message.type) {
			case 'touch':
				this.executeTouchEvent(message.data);
				break;
			case 'key':
				this.executeKeyEvent(message.data);
				break;
			case 'scroll':
				this.executeScrollEvent(message.data);
				break;
			case 'button_key_event': // 添加对按钮事件的处理
				this.executeButtonKeyEvent(message);
				break;
			case 'ime_text':
				this.executeImeText(message.data);
				break;
			default:
				console.warn('未知的广播消息类型:', message.type);
		}
	}
	// 添加按钮事件处理方法
	private executeButtonKeyEvent(keyData : any) {
		try {
			// 直接创建 KeyCodeControlMessage
			const message = new KeyCodeControlMessage(
				keyData.action,
				keyData.keycode,
				keyData.repeat || 0,
				keyData.metaState || 0
			);

			this.listener.sendMessage(message);
			console.log(`执行按钮按键事件: keycode=${keyData.keycode}, action=${keyData.action}`);
		} catch (error) {
			console.error('执行按钮按键事件失败:', error);
		}
	}
	private executeImeText(data : any) {
		const text = data?.text;
		if (!text) {
			return;
		}
		if (this.listener.mode === 'ws_room') {
			if (typeof this.listener.sendImeText === 'function') {
				this.listener.sendImeText(text);
			} else {
				console.warn('ws_room 被控端缺少 sendImeText 实现，文本输入被忽略');
			}
			return;
		}
		request.post('/containers/transfer_text', {
			udid: this.phoneId,
			text
		}).catch((error) => {
			console.error('同步输入文本失败:', error);
		});
	}

	// 执行触摸事件 - 归一化坐标 * 从机自身 videoSize
	private executeTouchEvent(touchData : any) {
		try {
			if (!touchData.position || !touchData.position.normPoint) {
				console.error('无效的触摸数据（缺少 normPoint）:', touchData);
				return;
			}
			const screenInfo = this.player.getScreenInfo();
			if (!screenInfo) {
				console.error('无法获取屏幕信息');
				return;
			}
			const { width, height } = screenInfo.videoSize;
			const x = touchData.position.normPoint.x * width;
			const y = touchData.position.normPoint.y * height;
			const size = new Size(width, height);
			const point = new Point(x, y);
			const position = new Position(point, size);
			const message = new TouchControlMessage(
				touchData.action,
				touchData.pointerId || 0,
				position,
				touchData.pressure || 1.0,
				touchData.buttons || 0
			);
			const sr : any = (this.listener as any).streamReceiver;
			const wsroomWs : any = (this.listener as any).ws;
			console.info(`[executeTouchEvent] slave=${this.phoneId} mode=${(this.listener as any)?.mode}`,
				'action=', touchData.action,
				'norm=', touchData.position.normPoint,
				'decoded=(', x, y, ')', 'videoSize=', width, height,
				'scrcpyWs=', sr?.ws?.readyState, 'scrcpyQueued=', sr?.events?.length,
				'wsRoomWs=', wsroomWs?.readyState,
			);
			this.listener.sendMessage(message);
		} catch (error) {
			console.error('执行触摸事件失败:', error);
		}
	}

	// 执行按键事件
	private executeKeyEvent(keyData : any) {
		try {
			// 使用现有的按键发送方法
			if (this.listener.sendKeyEvent) {
				this.listener.sendKeyEvent(keyData.code, keyData.action, keyData.metaState);
			} else {
				// 备用方案：通过控制消息发送
				const KeyCodeControlMessage = require('../controlMessage/KeyCodeControlMessage').KeyCodeControlMessage;
				const message = new KeyCodeControlMessage(keyData.action, keyData.code, keyData.metaState);
				this.listener.sendMessage(message);
			}
			console.log(`执行按键事件: ${keyData.code} ${keyData.action}`);
		} catch (error) {
			console.error('执行按键事件失败:', error);
		}
	}

	// 执行滚动事件 - 归一化坐标
	private executeScrollEvent(scrollData : any) {
		try {
			if (!scrollData.position || !scrollData.position.normPoint) {
				console.error('无效的滚动数据（缺少 normPoint）:', scrollData);
				return;
			}
			const screenInfo = this.player.getScreenInfo();
			if (!screenInfo) {
				console.error('无法获取屏幕信息');
				return;
			}
			const { width, height } = screenInfo.videoSize;
			const x = scrollData.position.normPoint.x * width;
			const y = scrollData.position.normPoint.y * height;
			const size = new Size(width, height);
			const point = new Point(x, y);
			const position = new Position(point, size);
			const message = new ScrollControlMessage(
				position,
				scrollData.hScroll || 0,
				scrollData.vScroll || 0
			);
			this.listener.sendMessage(message);
			console.log(`执行滚动事件: hScroll=${scrollData.hScroll}, vScroll=${scrollData.vScroll}`);
		} catch (error) {
			console.error('执行滚动事件失败:', error);
		}
	}

	// 广播消息到其他设备
	private broadcastMessage(message : BroadcastMessage) {
		if ((window as any).electronAPI && typeof (window as any).electronAPI.broadcastControl === 'function' && this.isMasterDevice) {
			(window as any).electronAPI.broadcastControl({
				sourcePhoneId: this.phoneId,
				groupId: this.groupId,
				message: message
			});
			return;
		}
		// 网页端：发进程内 EventBus
		if (this.isMasterDevice) {
			const bus = BroadcastBus.getInstance();
			console.info('[FeaturedInteractionHandler][web-emit] master',
				this.phoneId, 'type=', message.type,
				'listeners=', bus.listenerCount());
			bus.emit({
				sourcePhoneId: this.phoneId,
				groupId: this.groupId,
				message,
				timestamp: Date.now(),
			});
			try { (window as any).__onGroupMasterActivity?.(); } catch (_e) {}
		} else {
			console.debug('[FeaturedInteractionHandler] broadcastMessage 被非主设备调用，忽略', this.phoneId);
		}
	}

	// 新增：判断滚轮事件是否来自级联选择器（放行该区域的滚动）
	private isCascaderArea(target : HTMLElement | null) : boolean {
		if (!target) return false;
		const cascaderSelectors = [
			'.custom-cascader-popper',
			'.el-cascader',
			'.el-cascader__dropdown',
			'.el-popover[placement="right"]'
		];
		return cascaderSelectors.some(selector => {
			return target.matches(selector) || target.closest(selector) !== null;
		});
	}

	// 恢复之前的滚轮事件处理方法
	public buildScrollEvent(event : WheelEvent, screenInfo : ScreenInfo, player : BasePlayer) : ControlMessage[] {
		const isCascaderScroll = this.isCascaderArea(event.target as HTMLElement);
		if (isCascaderScroll) {
			return [];
		}

		const isInAllowedArea = this.checkIfEventInAllowedArea(event.target as HTMLElement);
		if (!isInAllowedArea) {
			return [];
		}

		const messages : ControlMessage[] = [];
		const touchOnClient = InteractionHandler.buildTouchOnClient(event, screenInfo, this.player);

		if (touchOnClient && touchOnClient.touch && touchOnClient.touch.position) {
			try {
				const originalPosition = touchOnClient.touch.position;
				const videoSize = screenInfo.videoSize;
				const normX = originalPosition.point.x / videoSize.width;
				const normY = originalPosition.point.y / videoSize.height;

				const hScroll = event.deltaX > 0 ? -1 : event.deltaX < 0 ? 1 : 0;
				const vScroll = event.deltaY > 0 ? -1 : event.deltaY < 0 ? 1 : 0;
				const time = Date.now();

				if (
					!this.lastScrollEvent ||
					time - this.lastScrollEvent.time > FeaturedInteractionHandler.SCROLL_EVENT_THROTTLING_TIME ||
					this.lastScrollEvent.vScroll !== vScroll ||
					this.lastScrollEvent.hScroll !== hScroll
				) {
					this.lastScrollEvent = { time, hScroll, vScroll };
					const scrollMessage = new ScrollControlMessage(originalPosition, hScroll, vScroll);
					messages.push(scrollMessage);

					if (this.isMasterDevice) {
						this.broadcastMessage({
							type: 'scroll',
							sourcePhoneId: this.phoneId,
							timestamp: Date.now(),
							data: {
								position: { normPoint: { x: normX, y: normY } },
								hScroll,
								vScroll
							}
						});
					}
				}
			} catch (error) {
				console.error('处理滚动事件失败:', error);
			}
		}
		return messages;
	}

	// 新增：校验事件源是否在允许的元素范围内
	private checkIfEventInAllowedArea(target : HTMLElement | null) : boolean {
		if (!target) return false;
		return this.ALLOWED_SCROLL_SELECTORS.some(selector => {
			return target.matches(selector) || target.closest(selector) !== null;
		});
	}

	// 修改 onInteraction 方法中的消息发送逻辑
	protected onInteraction(event : MouseEvent | TouchEvent | WheelEvent) : void {
		let messages : ControlMessage[] = [];

		// 处理事件
		if (event instanceof WheelEvent) {
			const isCascaderScroll = this.isCascaderArea(event.target as HTMLElement);
			if (isCascaderScroll) {
				return;
			}
			const screenInfo = this.player.getScreenInfo();
			if (screenInfo) {
				messages = this.buildScrollEvent(event, screenInfo, this.player);
			}
		} else if ((event instanceof MouseEvent) || event instanceof TouchEvent) {
			messages = this.generateOriginalMessages(event);
		}

		// 发送消息到当前设备
		messages.forEach((message) => {
			try {
				this.listener.sendMessage(message);
			} catch (error) {
				console.error('消息发送失败', error);
			}
		});

		// 如果是主设备，广播触摸和鼠标事件
		if (this.isMasterDevice && (event instanceof MouseEvent || event instanceof TouchEvent)) {
			this.broadcastTouchEvents(event, messages);
		}

		if (event.cancelable && !(event instanceof WheelEvent && this.isCascaderArea(event.target as HTMLElement))) {
			event.preventDefault();
		}
		if (!(event instanceof WheelEvent && this.isCascaderArea(event.target as HTMLElement))) {
			event.stopPropagation();
		}
	}

	// 广播触摸事件：归一化坐标
	private broadcastTouchEvents(event : MouseEvent | TouchEvent, messages : ControlMessage[]) {
		messages.forEach((message) => {
			if (message instanceof TouchControlMessage) {
				try {
					const originalPosition = message.position;
					const videoSize = originalPosition.screenSize;
					if (!videoSize || !videoSize.width || !videoSize.height) {
						console.error('无法获取屏幕信息，跳过广播');
						return;
					}
					const normX = originalPosition.point.x / videoSize.width;
					const normY = originalPosition.point.y / videoSize.height;
					this.broadcastMessage({
						type: 'touch',
						sourcePhoneId: this.phoneId,
						timestamp: Date.now(),
						data: {
							action: message.action,
							pointerId: message.pointerId,
							position: { normPoint: { x: normX, y: normY } },
							pressure: message.pressure || 1.0,
							buttons: message.buttons || 0
						}
					});
				} catch (error) {
					console.error('广播触摸事件失败:', error, message);
				}
			}
		});
	}

	// 键盘事件处理
	protected onKey(event : KeyboardEvent) : void {
		// 处理功能键
		const functionKeys = [
			'Enter', 'Tab', 'Escape', 'ArrowUp', 'ArrowDown',
			'ArrowLeft', 'ArrowRight', 'Delete', 'Home', 'End'
		];

		if (functionKeys.includes(event.key)) {
			event.preventDefault();
			const metaState = event.shiftKey ? 1 : 0;

			if (this.listener.sendKeyEvent) {
				this.listener.sendKeyEvent(event.code, event.type as 'down' | 'up', metaState);
			}
		}

		// 如果是主设备，广播按键事件
		if (this.isMasterDevice) {
			this.broadcastMessage({
				type: 'key',
				sourcePhoneId: this.phoneId,
				timestamp: Date.now(),
				data: {
					code: event.code,
					action: event.type === 'keydown' ? 'down' : 'up',
					metaState: this.getMetaState(event)
				}
			});
		}
	}

	// 获取修饰键状态
	private getMetaState(event : KeyboardEvent) : number {
		let metaState = 0;
		if (event.shiftKey) metaState |= 1;
		if (event.altKey) metaState |= 2;
		if (event.ctrlKey) metaState |= 4;
		if (event.metaKey) metaState |= 8;
		return metaState;
	}

	// 原始消息生成逻辑
	private generateOriginalMessages(event : MouseEvent | TouchEvent) : ControlMessage[] {
		let messages : ControlMessage[] = [];
		let storage : Map<number, TouchControlMessage>;

		if (!this.player) {
			console.error('当前实例没有player属性');
			return messages;
		}

		const currentScreenInfo = this.player.getScreenInfo();
		if (!currentScreenInfo) {
			return messages;
		}

		if (event instanceof MouseEvent) {
			if (event.target !== this.tag) return messages;

			if (window['WheelEvent'] && event instanceof WheelEvent) {
				messages = this.buildScrollEvent(event, currentScreenInfo, this.player);
			} else {
				storage = this.storedFromMouseEvent;
				messages = this.buildTouchEvent(
					event,
					currentScreenInfo,
					storage,
					this.player
				) as unknown as ControlMessage[];
			}
		} else if (window['TouchEvent'] && event instanceof TouchEvent) {
			if (event.target !== this.tag) return messages;
			storage = this.storedFromTouchEvent;
			messages = this.formatTouchEvent(
				event,
				currentScreenInfo,
				storage,
				this.player
			) as unknown as ControlMessage[];
		}
		return messages;
	}

	private onMouseEnter = () : void => {
		this.over = true;
	};

	private onMouseLeave = () : void => {
		this.lastPosition = undefined;
		this.over = false;
		this.storedFromMouseEvent.forEach((message) => {
			this.listener.sendMessage(InteractionHandler.createEmulatedMessage(MotionEvent.ACTION_UP, message));
		});
		this.storedFromMouseEvent.clear();
		this.clearCanvas();
	};

	public release() : void {
		super.release();
		this.tag.removeEventListener('mouseleave', this.onMouseLeave);
		this.tag.removeEventListener('mouseenter', this.onMouseEnter);
		this.storedFromMouseEvent.clear();

		// 取消广播注册
		if ((window as any).electronAPI) {
			(window as any).electronAPI.unregisterBroadcast({
				phoneId: this.phoneId,
				groupId: this.groupId
			});
			(window as any).electronAPI.removeBroadcastControlListener();
		}
		// 网页端：解绑 EventBus 订阅
		if (this.broadcastBusOff) {
			this.broadcastBusOff();
			this.broadcastBusOff = undefined;
		}
	}
}
